import React from "react";

import * as Styled from "./styles";

import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Modal,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import { Controller, useForm } from "react-hook-form";

import SignatureCanvas from "react-signature-canvas";

import { useAuth } from "shared/providers/auth";
import toast from "react-hot-toast";
import { IReferral } from "shared/types/Referral";
import { sendReferral } from "requests/Referral";
import { PostSubmitModal } from "../PostSubmitModal";
import { pdfDownload } from "requests/PdfDownload";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/pt-br";

interface IProps {
  open: boolean;
  close: (open: boolean) => void;
  pacId: number;
  pacFantasy?: string;
  onCompleteOrto?: () => void;
  emailPatient?: string;
  preSelectedProcedure?: "profilaxia" | "raio_x" | "avaliacao" | null;
  requiredProcedures?: ("profilaxia" | "raio_x" | "avaliacao")[];
}

const procedureLabel = (p: string) => {
  switch (p) {
    case "profilaxia":
      return "Profilaxia";
    case "raio_x":
      return "Panorâmica";
    case "avaliacao":
      return "Avaliação Clínica";
    default:
      return p;
  }
};

const ReferralReferrals: React.FC<IProps> = ({
  open,
  close,
  pacId,
  pacFantasy,
  onCompleteOrto,
  emailPatient,
  preSelectedProcedure,
  requiredProcedures = [],
}) => {
  const { user } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<IReferral>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const [signatureDoctor, setSignatureDoctor] = React.useState<any>();
  const [signaturePatient, setSignaturePatient] = React.useState<any>();
  const [report, setReport] = React.useState<boolean>(false);
  const [showPostModal, setShowPostModal] = React.useState(false);
  const [idForm, setIdForm] = React.useState<number>(0);

  const isMandatory =
    requiredProcedures.length > 0 || !!preSelectedProcedure;

  React.useEffect(() => {
    if (!isMandatory) return;

    const procs: string[] = [];
    if (preSelectedProcedure) {
      procs.push(procedureLabel(preSelectedProcedure));
    }
    requiredProcedures.forEach((p) => {
      const label = procedureLabel(p);
      if (!procs.includes(label)) procs.push(label);
    });

    if (procs.length > 0) {
      setValue("descricao", procs.join(" e "));
    }
  }, [preSelectedProcedure, requiredProcedures, setValue, isMandatory]);

  const sigCanvasPatient = React.useRef<SignatureCanvas>(null);
  const sigCanvasDentist = React.useRef<SignatureCanvas>(null);
  const sigBoxRef = React.useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = React.useState<number>(0);

  React.useEffect(() => {
    if (!open) return;

    const measure = () => {
      if (sigBoxRef.current) {
        setCanvasWidth(sigBoxRef.current.clientWidth - 2);
      }
    };

    const timer = setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const compressSignature = (canvas: HTMLCanvasElement, maxWidth = 400): string => {
    const trimmed = canvas;
    const scale = trimmed.width > maxWidth ? maxWidth / trimmed.width : 1;
    const resized = document.createElement("canvas");
    resized.width = trimmed.width * scale;
    resized.height = trimmed.height * scale;
    const ctx = resized.getContext("2d");
    if (ctx) {
      ctx.drawImage(trimmed, 0, 0, resized.width, resized.height);
    }
    return resized.toDataURL("image/png");
  };

  const saveSignaturePatient = () => {
    if (sigCanvasPatient.current) {
      const trimmed = sigCanvasPatient.current.getTrimmedCanvas();
      setSignaturePatient(compressSignature(trimmed));
    }
  };

  const saveSignatureDentist = () => {
    if (sigCanvasDentist.current) {
      const trimmed = sigCanvasDentist.current.getTrimmedCanvas();
      setSignatureDoctor(compressSignature(trimmed));
    }
  };

  const clearSignaturePatient = () => {
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
  };

  const clearSignatureDentits = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
  };

  const onSubmit = async (data: IReferral) => {
    if (!signaturePatient) {
      toast.error("Preencha a assinatura do paciente");
      return false;
    }
    if (!signatureDoctor) {
      toast.error("Preencha a assinatura do dentista");
      return false;
    }

    const fichaCompleta: IReferral = {
      ...data,
      assinatura_paciente: signaturePatient,
      assinatura_dentista: signatureDoctor,
      paciente_id: pacId,
      laudo: report,
      ...(pacFantasy ? { numero_cadastro: pacFantasy } : {}),
    };

    try {
      setLoading(true);
      const { status, data: dataForm } = await sendReferral(
        fichaCompleta,
        user.data.id
      );

      if ([200, 201].includes(status)) {
        toast.success("Formulário enviado com sucesso");
        setIdForm(dataForm.data.NR_SEQ_CONTRATOFORM_CFOC as number);

        setLoading(false);
        setShowPostModal(true);
        onCompleteOrto?.();
      } else {
        setLoading(false);
        toast.error("Erro ao enviar formulário");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onDownload = async () => {
    try {
      const { status, data } = await pdfDownload(
        { paciente_id: pacId, form_type: "encaminhamento" },
        user.data.id
      );

      if ([200, 201].includes(status)) {
        const base64 = data.data;

        //@ts-ignore
        if (window && window.ReactNativeWebView) {
          //@ts-ignore
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "downloadBase64File",
              value: base64,
            })
          );
        } else {
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "encaminhamento.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        toast.success("PDF baixado com sucesso");
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao baixar o PDF");
    }
  };

  return (
    <Modal open={open} onClose={() => close(false)}>
      <Box sx={{ backgroundColor: "white", height: "100%", width: "100%" }}>
        <Container maxWidth="xl" sx={{ overflow: "scroll", height: "100%" }}>
          <ReturnButton title="Encaminhamento" onClick={close} />

          {isMandatory && requiredProcedures.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Encaminhamento obrigatório para:{" "}
              {requiredProcedures.map((p) => procedureLabel(p)).join(" e ")}
              . Preencha e assine para continuar.
            </Alert>
          )}
          {isMandatory && preSelectedProcedure && requiredProcedures.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Encaminhamento obrigatório para{" "}
              {procedureLabel(preSelectedProcedure)}.
              Preencha e assine para continuar.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <p>Encaminho-lhe o(a) paciente para realização de:</p>

            <Controller
              name="descricao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={4}
                  label="Descrição do procedimento"
                  fullWidth
                  margin="normal"
                  error={!!errors.descricao}
                  helperText={
                    isMandatory
                      ? "Procedimento(s) obrigatório(s) — campo não editável"
                      : errors.descricao?.message
                  }
                  InputProps={{
                    readOnly: isMandatory,
                  }}
                  sx={isMandatory ? {
                    "& .MuiInputBase-root": {
                      backgroundColor: "#f5f5f5",
                    },
                  } : undefined}
                />
              )}
            />

            {!isMandatory && (
              <Controller
                name="destinatario"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Destinatário"
                    fullWidth
                    margin="normal"
                    error={!!errors.destinatario}
                    helperText={errors.destinatario?.message}
                  />
                )}
              />
            )}

            <FormGroup>
              <FormControlLabel
                control={
                  <Styled.Check
                    checked={report}
                    onChange={() => setReport(!report)}
                  />
                }
                sx={{ marginTop: "15px" }}
                label="Solicitamos laudo que conste a data de início e término do tratamento a ser realizado, para que possamos, com segurança e qualidade, dar continuidade ao tratamento ortodôntico que o(a) paciente realiza em nossa clínica."
              />
            </FormGroup>

            {!isMandatory && (
              <Controller
                name="data_observacao"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <p style={{ marginTop: "20px" }}>
                      Para que não haja atraso no tratamento anual, o paciente
                      deverá retornar em:
                    </p>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="pt-br"
                    >
                      <DatePicker
                        label="Selecione a data"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(newValue) => {
                          field.onChange(
                            newValue ? newValue.format("YYYY-MM-DD") : null
                          );
                        }}
                        format="DD/MM/YYYY"
                        minDate={dayjs(new Date())}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            margin: "normal",
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </>
                )}
              />
            )}

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Paciente ou Responsável
                <Button onClick={clearSignaturePatient} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature" ref={sigBoxRef}>
                {canvasWidth > 0 && (
                  <SignatureCanvas
                    onEnd={saveSignaturePatient}
                    ref={sigCanvasPatient}
                    penColor="black"
                    canvasProps={{
                      height: 120,
                      className: "sigCanvas",
                      width: canvasWidth,
                    }}
                  />
                )}
              </div>
            </Styled.BoxClinicalSignature>

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Dentista
                <Button onClick={clearSignatureDentits} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature">
                {canvasWidth > 0 && (
                  <SignatureCanvas
                    onEnd={saveSignatureDentist}
                    ref={sigCanvasDentist}
                    penColor="black"
                    canvasProps={{
                      height: 120,
                      className: "sigCanvas",
                      width: canvasWidth,
                    }}
                  />
                )}
              </div>
            </Styled.BoxClinicalSignature>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress color="inherit" /> : "Enviar"}
            </Button>
          </Box>
        </Container>

        <PostSubmitModal
          open={showPostModal}
          onClose={() => {
            setShowPostModal(false);
            close(false);
          }}
          onDownload={onDownload}
          onSendWhatsApp={() => {
            toast.success("Link enviado no WhatsApp");
          }}
          defaultEmail={emailPatient as string}
          formId={idForm}
          userId={user.data.id}
          userWpp={""}
        />
      </Box>
    </Modal>
  );
};

export { ReferralReferrals };
